import React, { useContext, useState, useEffect } from "react";
import { useMutation } from "@apollo/react-hooks";

import { Text, AddressForm, MobileContext } from "components/common";
import { REFETCH_ADDRESSES, UPDATE_ADDRESS } from "./gql-statements";
const parser = require("parse-address");

const defaultAddressErrorMessage = "Cannot update your address at this time. Please try again later.";

const parseAddress = (address) => {
  const {
    street: bStreet,
    city: bCity,
    state: bState,
    zip: bZip,
  } = address;
  const parsedAddress = parser.parseLocation(`${bStreet}, ${bCity}, ${bState} ${bZip}`);

  const {
    number,
    street,
    type,
    sec_unit_num,
    sec_unit_type,
  } = parsedAddress || {};

  const parsedStreet1 = [number, street, type].filter((s) => s).join(" ").toUpperCase();
  const parsedStreet2 = [sec_unit_type, sec_unit_num].filter((s) => s).join(" ").toUpperCase();

  return {
    street1: parsedStreet1 || bStreet,
    street2: parsedStreet2 || "",
    city: bCity,
    state: bState,
    zip: bZip,
  };
};

const Address = ({
  user,
  setSuccess,
  resetSuccess,
  setError,
  SettingsInputContainer,
  SettingsButton,
}) => {
  const parsedAddress = parseAddress(user.billing_address);
  const {
    street1: parsedStreet1,
    street2: parsedStreet2,
    city: parsedCity,
    state: parsedState,
    zip: parsedZip,
  } = parsedAddress || {};

  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [updateAddress] = useMutation(UPDATE_ADDRESS);

  const [street, setStreet] = useState(parsedStreet1 || "");
  const [unitNumber, setUnitNumber] = useState(parsedStreet2 || "");
  const [city, setCity] = useState(parsedCity || "");
  const [state, setState] = useState(parsedState || "");
  const [zip, setZip] = useState(parsedZip || "");
  const { isMobile } = useContext(MobileContext);

  useEffect(() => {
    if (street && city && zip && state) {
      setErrorMessage("");
    }
  }, [street, city, zip, state]);

  const renderBillingAddress = () => (
    <AddressForm
      style={{ maxWidth: "400px" }}
      street={street}
      city={city}
      state={state}
      zip={zip}
      unitNumber={unitNumber}
      setStreet={setStreet}
      setCity={setCity}
      setState={setState}
      setZip={setZip}
      setUnitNumber={setUnitNumber}
      loading={loading}
      setLoading={setLoading}
      setErrorMessage={setErrorMessage}
      SettingsInputContainer={SettingsInputContainer}
    />
  );

  const save = () => {
    if (!isMobile) {
      resetSuccess();
    }
    if (!(street && city && zip && state)){
      setErrorMessage("A valid street, city, state, and zip are required");
      return;
    }

    setErrorMessage("");
    setLoading(true);
    // track("DASHBOARD_SETTINGS_ADDRESS_CHANGE_SUBMITTED");
    const addressPayload = {
      street,
      city,
      state,
      zip,
      unit_number: unitNumber,
    };
    updateAddress({
      variables: {
        address: addressPayload,
      },
      refetchQueries: [{ query: REFETCH_ADDRESSES }],
      awaitRefetchQueries: true,
      // onCompleted: data => {}
    })
      .then((res) => {
        const { status, displayMessage, address } = res?.data?.updateUserAddressSelfService || {};
        if (status !== "SUCCESS") {
          setErrorMessage(displayMessage || defaultAddressErrorMessage)
          setLoading(false);
          return { success: false };
        }
        if (!isMobile) {
          setSuccess();
        }

        const {
          normalized_street,
          normalized_city,
          normalized_subdivision,
          normalized_postal_code,
        } = address;

        const parsedUpdatedAddress = parseAddress({
          street: normalized_street,
          city: normalized_city,
          state: normalized_subdivision,
          zip: normalized_postal_code,
        });

        setStreet(parsedUpdatedAddress.street1 || "");
        setUnitNumber(parsedUpdatedAddress.street2 || "");
        setCity(parsedUpdatedAddress.city || "");
        setState(parsedUpdatedAddress.state || "");
        setZip(parsedUpdatedAddress.zip || "");

        setLoading(false);
        return { success: true };
      })
      .catch(() => {
        setErrorMessage(defaultAddressErrorMessage);
        if (!isMobile) {
          setError("One or more of your addresses failed to save");
        }
        setLoading(false);
        return { success: false };
      });
  };

  return (
    <div className="mt-4">
      <div>
        <Text weight="font-bold" className="mb-1">
          Address
        </Text>
        {renderBillingAddress()}
      </div>
      <Text className={errorMessage ? "text-red-600 p-2" : "hidden"}>
        {errorMessage}
      </Text>
      <SettingsButton
        onClick={save}
        className={loading ? "disabled opacity-50 mt-5" : "mt-5"}
        disabled={loading}
      >
        Save address
      </SettingsButton>
    </div>
  );
};

export default Address;
